import React, { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ClassFilterProvider } from "@/contexts/ClassFilterContext";
import { Toaster } from "@/components/ui/sonner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useVisitTrail } from "@/hooks/useVisitTrail";
import { Menu } from "lucide-react";
import LoginPage from "@/pages/LoginPage";
import JoinPage from "@/pages/JoinPage";
import ActivateLoginPage from "@/pages/ActivateLoginPage";
import DemoPage from "@/pages/DemoPage";
import CATDemoPage from "@/pages/CATDemoPage";
import BrokerDemoPage from "@/pages/BrokerDemoPage";
import ClaimsDemoPage from "@/pages/ClaimsDemoPage";
import PackPortalPage from "@/pages/PackPortalPage";
import Sidebar from "@/components/layout/Sidebar";
import TopBar from "@/components/layout/TopBar";
import CommandPalette from "@/components/CommandPalette";
import AIChatPanel from "@/components/AIChatPanel";
import GuidedPath from "@/components/GuidedPath";
import AuthCallback from "@/pages/AuthCallback";
import LandingPage from "@/pages/LandingPage";
import WatchPage from "@/pages/WatchPage";
import StatusPage from "@/pages/StatusPage";
import PolicyholderPortalPage from "@/pages/PolicyholderPortalPage";
import SignPage from "@/pages/SignPage";
import SignaturesPage from "@/pages/SignaturesPage";
import InvoicesPage from "@/pages/InvoicesPage";

// Module imports (11 modules per IA document)
import TodayModule from "@/modules/TodayModule";
import NewBusinessModule from "@/modules/NewBusinessModule";
import UnderwritingModule from "@/modules/UnderwritingModule";
import RenewalsModule from "@/modules/RenewalsModule";
import ClaimsModule from "@/modules/ClaimsModule";
import PoliciesModule from "@/modules/PoliciesModule";
import DocumentsPage from "@/pages/DocumentsPage";
import PortfolioModule from "@/modules/PortfolioModule";
import FinanceModule from "@/modules/FinanceModule";
import ComplianceModule from "@/modules/ComplianceModule";
import MarketIntelModule from "@/modules/MarketIntelModule";
import MigrationToolkitPage from "@/pages/MigrationToolkitPage";
import ValidationHarnessPage from "@/pages/ValidationHarnessPage";
import KnowledgeModule from "@/modules/KnowledgeModule";
import SecurityPage from "@/pages/SecurityPage";
import PlatformTruthPage from "@/pages/PlatformTruthPage";
import MfaSetupCard from "@/components/MfaSetupCard";
import DistributionModule from "@/modules/DistributionModule";
import DemoStudioModule from "@/modules/DemoStudioModule";
import PresentDemoPage from "@/pages/PresentDemoPage";
import AdminModule from "@/modules/AdminModule";
import MaturityIndexModule from "@/modules/MaturityIndexModule";
import OperatingModelModule from "@/modules/OperatingModelModule";
import CommandModule from "@/modules/CommandModule";
import RateTablesModule from "@/modules/RateTablesModule";
import AutonomousModule from "@/modules/AutonomousModule";
import GovernanceModule from "@/modules/GovernanceModule";
import ClientGoldenPage from "@/pages/ClientGoldenPage";
import BrokerDashboardPage from "@/pages/BrokerDashboardPage";
import BrokerDataGapsPage from "@/pages/BrokerDataGapsPage";
import BrokerageProfilePage from "@/pages/BrokerageProfilePage";
import BrokerClientsPage from "@/pages/BrokerClientsPage";
import BrokerClientFilePage from "@/pages/BrokerClientFilePage";
import QuoteDetailsPage from "@/pages/QuoteDetailsPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F5F7FA' }}>
        <div className="text-center">
          <span className="text-3xl tracking-[0.08em] uppercase font-bold" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
            <span style={{ color: '#0B1E3D' }}>fr</span>
            <span style={{ background: 'linear-gradient(135deg, #FFB347, #FF9933, #D96E00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>AI</span>
            <span style={{ color: '#0B1E3D' }}>me</span>
          </span>
          <p className="mt-3 text-sm" style={{ color: '#5C6B7A' }}>Loading...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const defaultPath = landingFor(user);

  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);
  useVisitTrail();

  // ⌘K keyboard shortcut
  const handleKeyDown = useCallback((e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      setPaletteOpen(prev => !prev);
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'j') {
      e.preventDefault();
      setChatOpen(prev => !prev);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // MFA enforcement gate (ruling mfa_reversal, 7 Sep 2026) — owner/admin must enrol before using the app
  if (user?.mfa_setup_required) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#F5F7FA' }} data-testid="mfa-forced-gate">
        <div className="w-full max-w-lg p-6 rounded-lg border bg-white space-y-4" style={{ borderColor: '#E4E9EF' }}>
          <div className="text-lg font-bold" style={{ color: '#0D1B2A' }}>Set up multi-factor authentication</div>
          <MfaSetupCard forced />
        </div>
      </div>
    );
  }

  // Role allow-list (mirrors backend core/roles.py + Sidebar ROLE_NAV): a role named here gets only its own modules
  const allowed = ROLE_ROUTES[user?.role];
  const onPath = (p) => location.pathname === p || location.pathname.startsWith(p + '/');
  if (allowed && !allowed.some(onPath)) return <Navigate to={landingFor(user)} replace />;
  if ((ROLE_DENY[user?.role] || []).some(onPath)) return <Navigate to={landingFor(user)} replace />;
  const adminPage = Object.keys(ADMIN_PAGE_ROLES).find(onPath);
  if (adminPage && user && !ADMIN_PAGE_ROLES[adminPage].includes(user.role)) return <Navigate to={landingFor(user)} replace />;

  return (
    <div className="min-h-screen" style={{ background: '#F5F7FA' }}>
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <div className={`${mobileNavOpen ? 'block' : 'hidden'} lg:block`}>
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>
      {mobileNavOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setMobileNavOpen(false)} data-testid="mobile-nav-backdrop" />
      )}
      <button onClick={() => setMobileNavOpen(o => !o)} data-testid="mobile-nav-toggle"
        className="lg:hidden fixed bottom-4 left-4 z-[60] w-12 h-12 rounded-full flex items-center justify-center text-white shadow-xl"
        style={{ background: '#0D1B2A' }} aria-label="Open navigation">
        <Menu className="w-5 h-5" />
      </button>
      <div className={`transition-all duration-200 ml-0 ${collapsed ? 'lg:ml-16' : 'lg:ml-60'} ${chatOpen ? 'mr-[400px]' : ''}`}>
        <TopBar onOpenSearch={() => setPaletteOpen(true)} />
        <GuidedPath />
        <main id="main-content" className="p-5 max-w-[1400px]">
          <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Navigate to={defaultPath} replace />} />
            {/* 11 Modules */}
            <Route path="/today" element={<RoleGate><TodayModule /></RoleGate>} />
            <Route path="/new-business" element={<NewBusinessModule />} />
            <Route path="/underwriting" element={<RoleGate><UnderwritingModule /></RoleGate>} />
            <Route path="/renewals" element={<RenewalsModule />} />
            <Route path="/claims" element={<ClaimsModule />} />
            <Route path="/claims/:claimId" element={<ClaimsModule />} />
            <Route path="/policies" element={<PoliciesModule />} />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/signatures" element={<RoleGate><SignaturesPage /></RoleGate>} />
            <Route path="/portfolio" element={<RoleGate><PortfolioModule /></RoleGate>} />
            <Route path="/finance" element={<RoleGate><FinanceModule /></RoleGate>} />
            <Route path="/compliance" element={<RoleGate><ComplianceModule /></RoleGate>} />
            <Route path="/market-intel" element={<AdminGate><MarketIntelModule /></AdminGate>} />
            <Route path="/migration" element={<RoleGate><MigrationToolkitPage /></RoleGate>} />
            <Route path="/validation" element={<RoleGate><ValidationHarnessPage /></RoleGate>} />
            <Route path="/knowledge" element={<RoleGate><KnowledgeModule /></RoleGate>} />
            <Route path="/security" element={<RoleGate><SecurityPage /></RoleGate>} />
            <Route path="/platform-truth" element={<AdminGate><PlatformTruthPage /></AdminGate>} />
            <Route path="/distribution" element={<RoleGate><DistributionModule /></RoleGate>} />
            <Route path="/demo-studio" element={<RoleGate><DemoStudioModule /></RoleGate>} />
            <Route path="/admin" element={<AdminGate><AdminModule /></AdminGate>} />
            <Route path="/maturity" element={<RoleGate><MaturityIndexModule /></RoleGate>} />
            <Route path="/operating-model" element={<RoleGate><OperatingModelModule /></RoleGate>} />
            <Route path="/command" element={<RoleGate><CommandModule /></RoleGate>} />
            <Route path="/rates" element={<RoleGate><RateTablesModule /></RoleGate>} />
            <Route path="/autonomous" element={<RoleGate><AutonomousModule /></RoleGate>} />
            <Route path="/governance" element={<RoleGate><GovernanceModule /></RoleGate>} />
            <Route path="/client/:entityId" element={<ClientGoldenPage />} />
            {/* Broker portal */}
            <Route path="/broker" element={<BrokerDashboardPage />} />
            <Route path="/data-gaps" element={<BrokerDataGapsPage />} />
            <Route path="/brokerage" element={<BrokerageProfilePage />} />
            <Route path="/invoices" element={<InvoicesPage />} />
            <Route path="/clients" element={<BrokerClientsPage />} />
            <Route path="/clients/:entityId" element={<BrokerClientFilePage />} />
            {/* Quote details */}
            <Route path="/submissions/:submissionId" element={<QuoteDetailsPage />} />
            {/* Legacy route redirects */}
            <Route path="/dashboard" element={<Navigate to="/today" replace />} />
            <Route path="/submissions" element={<Navigate to="/new-business?tab=pipeline" replace />} />
            <Route path="/search" element={<Navigate to="/new-business?tab=submit" replace />} />
            <Route path="/class-submission" element={<Navigate to="/new-business?tab=quote" replace />} />
            <Route path="/referrals" element={<Navigate to="/underwriting" replace />} />
            <Route path="/renewal-insights" element={<Navigate to="/renewals?tab=insights" replace />} />
            <Route path="/cat-mode" element={<Navigate to="/claims?tab=cat-mode" replace />} />
            <Route path="/heatmap" element={<Navigate to="/portfolio?tab=heatmap" replace />} />
            <Route path="/territory" element={<Navigate to="/portfolio?tab=territory" replace />} />
            <Route path="/broker-scorecard" element={<Navigate to="/portfolio?tab=brokers" replace />} />
            <Route path="/compliance-calendar" element={<Navigate to="/compliance?tab=calendar" replace />} />
            <Route path="/broker-intelligence" element={<Navigate to="/distribution" replace />} />
            <Route path="/onboarding" element={<Navigate to="/distribution?tab=onboarding" replace />} />
            <Route path="/config" element={<Navigate to="/admin" replace />} />
            <Route path="/mars" element={<Navigate to="/admin?tab=mars" replace />} />
            <Route path="/ai-operations" element={<Navigate to="/admin?tab=ai-ops" replace />} />
            <Route path="/operating-modes" element={<Navigate to="/operating-model?tab=modes" replace />} />
            <Route path="/admin/operating-modes" element={<Navigate to="/operating-model?tab=modes" replace />} />
            <Route path="/data-quality" element={<Navigate to="/admin?tab=data-quality" replace />} />
            <Route path="/enrichment" element={<Navigate to="/admin?tab=enrichment" replace />} />
            <Route path="/corporate-groups" element={<Navigate to="/admin?tab=corp-groups" replace />} />
            <Route path="/email-pipeline" element={<Navigate to="/admin?tab=email" replace />} />
            <Route path="*" element={<Navigate to={defaultPath} replace />} />
          </Routes>
          </ErrorBoundary>
        </main>
      </div>
      <CommandPalette isOpen={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <AIChatPanel isOpen={chatOpen} onClose={() => setChatOpen(false)} />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <ClassFilterProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </ClassFilterProvider>
    </AuthProvider>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingOrApp />} />
      <Route path="/demo" element={<StaffDemoGate><DemoPage /></StaffDemoGate>} />
      <Route path="/watch" element={<WatchPage />} />
      <Route path="/demo/cat" element={<StaffDemoGate><CATDemoPage /></StaffDemoGate>} />
      <Route path="/demo/broker" element={<StaffDemoGate><BrokerDemoPage /></StaffDemoGate>} />
      <Route path="/demo/claims" element={<StaffDemoGate><ClaimsDemoPage /></StaffDemoGate>} />
      <Route path="/pack/:token" element={<PackPortalPage />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/my/:token" element={<PolicyholderPortalPage />} />
      <Route path="/sign/:token" element={<SignPage />} />
      <Route path="/present" element={<ProtectedRoute><PresentDemoPage /></ProtectedRoute>} />
      <Route path="/login" element={<LoginPageWrapper />} />
      <Route path="/join/:token" element={<JoinPage />} />
      <Route path="/activate/:token" element={<ActivateLoginPage />} />
      <Route path="/*" element={<ProtectedRoute><AppLayout /></ProtectedRoute>} />
    </Routes>
  );
}

// Routes an investor may open. Everything else sends them back to the portfolio — the same
// allow-list the backend enforces, mirrored so they never see a screen that will only 403.
const INVESTOR_ROUTES = ['/portfolio', '/finance', '/maturity', '/operating-model', '/security'];
// Roles with an allow-list of modules (everything else redirects to their landing). Mirrors backend core/roles.py.
const ROLE_ROUTES = {
  claims_handler: ['/today', '/claims', '/policies', '/documents', '/signatures', '/knowledge', '/compliance', '/client', '/settings', '/profile'],
  actuary: ['/today', '/rates', '/portfolio', '/policies', '/knowledge', '/governance', '/maturity', '/validation', '/settings', '/profile'],
  investor: [...INVESTOR_ROUTES, '/settings', '/profile'],
};
// Read-only logins are never offered a page whose purpose is to create work
const ROLE_DENY = {
  regulator: ['/new-business', '/demo-studio', '/migration'],
};
// Admin-only pages and the roles that may open them (actuary may read governance / maturity)
const ADMIN_PAGE_ROLES = {
  '/governance': ['platform_owner', 'mga_admin', 'regulator', 'actuary'],
};

function landingFor(user) {
  if (user?.landing) return user.landing;   // backend core/team.landing_for — an executive opens on their Command Center lens
  const role = user?.role;
  if (role === 'broker') return '/broker';
  if (role === 'investor') return '/portfolio';
  if (role === 'claims_handler') return '/claims';
  if (role === 'actuary') return '/rates';
  if (role === 'underwriter') return '/underwriting';
  return '/today';
}

function LoginPageWrapper() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={landingFor(user)} replace />;
  return <LoginPage />;
}

function RoleGate({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user?.role === 'broker') return <Navigate to="/broker" replace />;
  // Allow-list, not deny-list: a role added in future gets nothing until it is named here.
  if (user?.role === 'investor' && !INVESTOR_ROUTES.some(p => pathname.startsWith(p)))
    return <Navigate to="/portfolio" replace />;
  return children;
}

// Demo pages are a prospect funnel: visitors may watch; signed-in brokers, regulators and
// investors are sent back to their own landing instead.
function StaffDemoGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user && ['broker', 'regulator', 'investor'].includes(user.role))
    return <Navigate to={landingFor(user)} replace />;
  return children;
}

function AdminGate({ children }) {
  const { user } = useAuth();
  if (user?.role === 'broker') return <Navigate to="/broker" replace />;
  if (user && !['platform_owner', 'mga_admin', 'regulator'].includes(user.role))
    return <Navigate to={landingFor(user)} replace />;
  return children;
}

function LandingOrApp() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={landingFor(user)} replace />;
  return <LandingPage />;
}

export default App;
